import type { components } from "@erp/api-client";

import { clearBranchContext, configureBranchContext } from "@/lib/branch/branch-store";
import { UnauthorizedError } from "./errors";
import { apiFetch } from "./http";

export type MfaLevel = components["schemas"]["MfaLevel"];
export type LoginSessionUser = components["schemas"]["SessionUser"];
export type SessionUser = components["schemas"]["SessionBootstrap"];

/**
 * Stable machine codes the auth endpoints return in problem+json bodies.
 * The mock handlers use these exact values; UI maps them to i18n keys.
 */
export const AUTH_CODES = {
  invalidCredentials: "auth.invalid_credentials",
  accountLocked: "auth.account_locked",
  rateLimited: "auth.rate_limited",
  weakPassword: "auth.weak_password",
  invalidToken: "auth.invalid_token",
  unauthenticated: "platform.unauthenticated",
} as const;

export type AuthCode = (typeof AUTH_CODES)[keyof typeof AUTH_CODES];

export type MfaMethod = components["schemas"]["MFARequiredResponse"]["methods"][number];

/**
 * What a password actually buys. An account with a second factor gets a
 * 10-minute pending session and a challenge instead of a session, so the
 * caller must branch rather than assume it is signed in.
 */
export type LoginOutcome =
  { kind: "session"; user: LoginSessionUser } | { kind: "mfa"; methods: readonly MfaMethod[] };

/**
 * POST /session. A 401 here means wrong credentials, not an expired session,
 * so the global unauthorized event is suppressed; the UnauthorizedError
 * (problem.code === AUTH_CODES.invalidCredentials) still propagates.
 *
 * A 202 answers `auth.mfa_required` rather than a user. That body is not an
 * error — the password was right — so it is returned as an outcome to branch
 * on, not thrown.
 */
export async function login(code: string, password: string): Promise<LoginOutcome> {
  const answer = await apiFetch<LoginSessionUser | components["schemas"]["MFARequiredResponse"]>(
    "/session",
    {
      method: "POST",
      body: { code, password },
      idempotency: "none",
      suppressUnauthorizedEvent: true,
    },
  );
  if ("mfa_required" in answer && answer.mfa_required) {
    return { kind: "mfa", methods: answer.methods };
  }
  return { kind: "session", user: answer as LoginSessionUser };
}

/**
 * POST /session/mfa. Exchanges the pending session for a real one using a
 * fresh six-digit step or one recovery code; both are single-use, and five
 * invalid attempts revoke the pending session outright.
 */
export async function challengeSessionMFA(code: string): Promise<LoginSessionUser> {
  return apiFetch<LoginSessionUser>("/session/mfa", {
    method: "POST",
    body: { code },
    idempotency: "none",
    suppressUnauthorizedEvent: true,
  });
}

/**
 * DELETE /sessions — every session of the signed-in user, this one included.
 * The cookie is cleared server-side, so the caller is signed out here too.
 */
export async function revokeAllSessions(): Promise<void> {
  try {
    await apiFetch<undefined>("/sessions", { method: "DELETE", responseBody: "none" });
  } finally {
    clearBranchContext();
  }
}

/** DELETE /session — always resolves on 2xx; server clears the cookie. */
export async function logout(): Promise<void> {
  try {
    await apiFetch<undefined>("/session", { method: "DELETE" });
  } finally {
    clearBranchContext();
  }
}

/**
 * GET /session probe. Resolves null when not signed in (expected 401 — the
 * global "erp:unauthorized" event is NOT dispatched for this probe).
 */
export async function currentSession(): Promise<SessionUser | null> {
  try {
    const session = await apiFetch<SessionUser>("/session", {
      suppressUnauthorizedEvent: true,
      // Never let a previous identity's selection influence bootstrap.
      branchScope: "company",
    });
    configureBranchContext(session);
    return session;
  } catch (error) {
    if (error instanceof UnauthorizedError) {
      clearBranchContext();
      return null;
    }
    throw error;
  }
}

/**
 * POST /password-resets — always 202 regardless of whether the email exists
 * (anti-enumeration), so this resolving proves nothing about the account.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiFetch<undefined>("/password-resets", {
    method: "POST",
    body: { email },
    idempotency: "none",
    responseBody: "none",
  });
}

/**
 * POST /password-resets/complete. Throws ApiError with
 * AUTH_CODES.invalidToken (bad/expired token) or AUTH_CODES.weakPassword
 * (with errors[] targeting the "password" field).
 */
export async function completePasswordReset(token: string, password: string): Promise<void> {
  await apiFetch<undefined>("/password-resets/complete", {
    method: "POST",
    body: { token, password },
    idempotency: "none",
  });
}
